import { useCallback, useEffect, useState } from 'react';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import {
  listAIPromptVersions,
  type AIPromptType,
  type AIPromptVersion,
} from '../../services/firebase';
import toast from 'react-hot-toast';

interface AIPromptHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptKey: AIPromptType | null;
  promptName: string;
  /**
   * Restore a historical version. The parent is responsible for snapshotting the
   * current live value first (so the restore is itself undoable) and writing the
   * historical text back to the live prompt. Resolves once the restore is saved.
   */
  onRestore: (version: AIPromptVersion) => Promise<void>;
}

function formatSavedAt(version: AIPromptVersion): string {
  const date = version.savedAt?.toDate();
  if (!date) return 'Saving…';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function savedByLabel(version: AIPromptVersion): string {
  return version.savedByEmail || version.savedBy || 'unknown';
}

export function AIPromptHistoryModal({
  isOpen,
  onClose,
  promptKey,
  promptName,
  onRestore,
}: AIPromptHistoryModalProps) {
  const [versions, setVersions] = useState<AIPromptVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState<AIPromptVersion | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<AIPromptVersion | null>(null);
  const [restoring, setRestoring] = useState(false);

  const loadVersions = useCallback(async () => {
    if (!promptKey) return;
    setLoading(true);
    try {
      const data = await listAIPromptVersions(promptKey);
      setVersions(data);
    } catch (error) {
      console.error('Failed to load prompt history:', error);
      toast.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [promptKey]);

  useEffect(() => {
    if (isOpen && promptKey) {
      loadVersions();
    } else {
      // Reset transient state when closed so a re-open starts clean.
      setViewing(null);
      setConfirmRestore(null);
    }
  }, [isOpen, promptKey, loadVersions]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleConfirmRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(true);
    try {
      await onRestore(confirmRestore);
      toast.success('Prompt restored from history');
      setConfirmRestore(null);
      // Refresh — the current-live snapshot taken during restore is now a version.
      await loadVersions();
    } catch (error) {
      console.error('Failed to restore prompt:', error);
      toast.error('Failed to restore prompt');
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`History: ${promptName}`}
      size="xl"
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : versions.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">
          No previous versions yet. A snapshot is saved automatically each time this
          prompt is changed.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {versions.map((version) => (
            <div
              key={version.id}
              className="flex items-start justify-between gap-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="font-medium text-gray-900">
                    {formatSavedAt(version)}
                  </span>
                  <span className="text-gray-400">·</span>
                  <span className="truncate text-gray-600">{savedByLabel(version)}</span>
                </div>
                {version.note ? (
                  <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                    {version.note}
                  </p>
                ) : (
                  <p className="mt-1 text-sm italic text-gray-300">No note</p>
                )}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button variant="secondary" size="sm" onClick={() => setViewing(version)}>
                  View
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmRestore(version)}
                >
                  Restore
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View a single version — read-only full text */}
      <Modal
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? `Version from ${formatSavedAt(viewing)}` : 'Version'}
        size="full"
      >
        {viewing && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-500">
                Saved by <span className="font-medium text-gray-700">{savedByLabel(viewing)}</span>
                {viewing.note ? ` · ${viewing.note}` : ''}
              </p>
              <Button variant="secondary" size="sm" onClick={() => handleCopy(viewing.promptText)}>
                Copy to clipboard
              </Button>
            </div>
            <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-sm text-gray-800">
              {viewing.promptText}
            </pre>
          </div>
        )}
      </Modal>

      {/* Confirm restore */}
      <Modal
        isOpen={!!confirmRestore}
        onClose={() => (restoring ? undefined : setConfirmRestore(null))}
        title="Restore this version?"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will replace the current live prompt. The current live version will be
            snapshotted first, so this is reversible.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setConfirmRestore(null)}
              disabled={restoring}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmRestore} loading={restoring}>
              Restore
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
}
